import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { API_URL } from '../constants/urls';

// Types matching backend schemas
export enum ResearchNoteType {
  INSIGHT = 'insight',
  RESEARCH = 'research',
  MEETING = 'meeting',
  ANALYSIS = 'analysis',
  RECOMMENDATION = 'recommendation',
  GENERAL = 'general',
}

export enum ResearchSourceType {
  MANUAL = 'manual',
  AUTOMATED = 'automated',
}

export enum ResearchVisibility {
  PRIVATE = 'private',
  PUBLIC = 'public',
}

export enum ResearchEntityType {
  SEGMENT = 'segment',
  PROPOSITION = 'proposition',
  POSITION = 'position',
  COMPANY = 'company',
  CONTACT = 'contact',
  ORGANIZATION = 'organization',
  OKR = 'okr',
}

export interface ResearchAttachment {
  id: string;
  research_note_id: string;
  attachment_type: string;
  attachment_data: any;
  attachment_metadata?: any;
  created_at: string;
}

export interface ResearchNote {
  id: string;
  organization_id: string;
  entity_type: ResearchEntityType;
  entity_id?: string;
  entity_name?: string;
  title: string;
  content: string;
  note_type: ResearchNoteType;
  source_type: ResearchSourceType;
  source_reference?: string;
  note_metadata: any;
  visibility: ResearchVisibility;
  is_pinned: boolean;
  created_by: string;
  created_by_name?: string;
  updated_by?: string;
  updated_by_name?: string;
  created_at: string;
  updated_at: string;
  attachments: ResearchAttachment[];
}

export interface ResearchNoteCreate {
  entity_type: ResearchEntityType;
  entity_id?: string;
  title: string;
  content: string;
  note_type?: ResearchNoteType;
  source_type?: ResearchSourceType;
  source_reference?: string;
  note_metadata?: any;
  visibility?: ResearchVisibility;
  is_pinned?: boolean;
  attachments?: {
    attachment_type: string;
    attachment_data: any;
    attachment_metadata?: any;
  }[];
}

export interface ResearchNoteUpdate {
  title?: string;
  content?: string;
  note_type?: ResearchNoteType;
  note_metadata?: any;
  visibility?: ResearchVisibility;
  is_pinned?: boolean;
}

export interface ResearchNoteListResponse {
  notes: ResearchNote[];
  total: number;
  page: number;
  page_size: number;
}

export interface ResearchFilters {
  entity_type?: ResearchEntityType;
  entity_id?: string;
  note_type?: ResearchNoteType;
  source_type?: ResearchSourceType;
  is_pinned?: boolean;
  search?: string;
  page?: number;
  page_size?: number;
}

class ResearchService {
  // List research notes with filters
  async listResearchNotes(
    organizationId: string,
    filters?: ResearchFilters,
  ): Promise<ResearchNoteListResponse> {
    try {
      const params = new URLSearchParams({
        organization_id: organizationId,
        ...(filters?.entity_type && { entity_type: filters.entity_type }),
        ...(filters?.entity_id && { entity_id: filters.entity_id }),
        ...(filters?.note_type && { note_type: filters.note_type }),
        ...(filters?.source_type && { source_type: filters.source_type }),
        ...(filters?.is_pinned !== undefined && {
          is_pinned: filters.is_pinned.toString(),
        }),
        ...(filters?.search && { search: filters.search }),
        ...(filters?.page && { page: filters.page.toString() }),
        ...(filters?.page_size && { page_size: filters.page_size.toString() }),
      });

      const response = await axios.get(`${API_URL}/research-notes?${params}`, {
        headers: getAuthHeaders(),
      });

      return response.data;
    } catch (error) {
      console.error('Error fetching research notes:', error);
      throw error;
    }
  }

  // Get a specific research note
  async getResearchNote(
    organizationId: string,
    noteId: string,
  ): Promise<ResearchNote> {
    try {
      const response = await axios.get(
        `${API_URL}/research-notes/${noteId}?organization_id=${organizationId}`,
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching research note:', error);
      throw error;
    }
  }

  // Create a new research note
  async createResearchNote(
    organizationId: string,
    data: ResearchNoteCreate,
  ): Promise<ResearchNote> {
    try {
      const requestData = {
        ...data,
        entity_id: data.entity_id || null, // Convert undefined to null
      };

      console.log('=== RESEARCH SERVICE DEBUG ===');
      console.log('Input data:', data);
      console.log('Request data being sent:', requestData);
      console.log(
        'note_type:',
        requestData.note_type,
        'type:',
        typeof requestData.note_type,
      );
      console.log(
        'source_type:',
        requestData.source_type,
        'type:',
        typeof requestData.source_type,
      );
      console.log(
        'visibility:',
        requestData.visibility,
        'type:',
        typeof requestData.visibility,
      );
      console.log('JSON.stringify result:', JSON.stringify(requestData));
      console.log('=== END SERVICE DEBUG ===');

      const response = await axios.post(
        `${API_URL}/research-notes?organization_id=${organizationId}`,
        requestData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error: any) {
      console.error('Error creating research note:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
      }
      throw error;
    }
  }

  // Update an existing research note
  async updateResearchNote(
    organizationId: string,
    noteId: string,
    data: ResearchNoteUpdate,
  ): Promise<ResearchNote> {
    try {
      const response = await axios.put(
        `${API_URL}/research-notes/${noteId}?organization_id=${organizationId}`,
        data,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error updating research note:', error);
      throw error;
    }
  }

  // Delete a research note
  async deleteResearchNote(
    organizationId: string,
    noteId: string,
  ): Promise<void> {
    try {
      await axios.delete(
        `${API_URL}/research-notes/${noteId}?organization_id=${organizationId}`,
        {
          headers: getAuthHeaders(),
        },
      );
    } catch (error) {
      console.error('Error deleting research note:', error);
      throw error;
    }
  }

  // Get all research notes for a specific entity
  async getEntityResearch(
    organizationId: string,
    entityType: ResearchEntityType,
    entityId: string,
    pinnedOnly = false,
  ): Promise<ResearchNote[]> {
    try {
      const params = new URLSearchParams({
        organization_id: organizationId,
        ...(pinnedOnly && { include_pinned_only: 'true' }),
      });

      const response = await axios.get(
        `${API_URL}/research-notes/entity/${entityType}/${entityId}?${params}`,
        {
          headers: getAuthHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching entity research:', error);
      throw error;
    }
  }

  // Export research notes (future enhancement)
  async exportResearchNotes(
    organizationId: string,
    filters?: ResearchFilters,
    format: 'json' | 'csv' = 'json',
  ): Promise<Blob> {
    try {
      const params = new URLSearchParams({
        organization_id: organizationId,
        format,
        ...(filters?.entity_type && { entity_type: filters.entity_type }),
        ...(filters?.entity_id && { entity_id: filters.entity_id }),
        ...(filters?.search && { search: filters.search }),
      });

      const response = await axios.get(
        `${API_URL}/research-notes/export?${params}`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob',
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error exporting research notes:', error);
      throw error;
    }
  }
}

export const researchService = new ResearchService();
